import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useQuery, useMutation } from 'react-apollo';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import ReactGA from 'react-ga';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

import { Button, Flex } from 'core/ui/atoms';
import Slide1 from 'johari/ui/components/Slide1';
import Slide2 from 'johari/ui/components/Slide2';
import Slide3 from 'johari/ui/components/Slide3';
import JohariLayout from 'johari/ui/layouts/JohariLayout';
import Loading from 'core/ui/components/Loading';
import Johari404 from 'johari/ui/components/404';
import { MAX_TRAITS } from 'johari/api/constants';

import {
  GET_JOHARI_USER,
  GET_CODE_BY_USER_ID,
} from 'johari/api/apollo/queries';
import { ADD_JOHARI_TEST } from 'johari/api/apollo/mutations';

const SLIDES = [Slide1, Slide2, Slide3];

const fpPromise = FingerprintJS.load();

const JohariPage = ({
  match: {
    params: { id },
  },
}) => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const params = Object.fromEntries(urlSearchParams.entries());
  const invite = params && params.invite;

  const [currentSlide, setCurrentSlide] = useState(0);
  const [traits, setTraits] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [fingerprint, setFingerprint] = useState(undefined);

  const { data, loading, error } = useQuery(GET_JOHARI_USER, {
    variables: {
      _id: invite,
    },
  });

  // const { data: codeData } = useQuery(GET_CODE_BY_USER_ID, {
  //   variables: {
  //     userId: invite,
  //   },
  // });

  const [addJohariTest] = useMutation(ADD_JOHARI_TEST);

  const { t } = useTranslation();

  useEffect(() => {
    (async () => {
      const fp = await fpPromise;
      const result = await fp.get();
      const visitorId = result.visitorId;
      setFingerprint(visitorId);
    })();
    ReactGA.pageview(window.location.pathname);
  }, []);

  useEffect(() => {
    if (!loading && data && fingerprint) {
      const fingerprints = data?.getJohariUser?.fingerprints || [];
      if (fingerprints.includes(fingerprint)) {
        setCurrentSlide(2);
      }
    }
  }, [loading, data, error, fingerprint]);

  const nextSlide = async () => {
    if (currentSlide === 1) {
      ReactGA.event({
        category: 'test',
        action: 'add_traits',
        label: data?.getJohariUser?._id,
      });
      setIsProcessing(true);
      try {
        await addJohariTest({
          variables: {
            input: {
              traits,
              _id: invite,
              fingerprint,
            },
          },
        });
        setIsProcessing(false);
        setCurrentSlide(currentSlide + 1);
      } catch (e) {
        console.log(e);
        setIsProcessing(false);
      }
    } else {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const toggleTrait = (trait) => {
    if (traits.includes(trait)) {
      setTraits(traits.filter((t) => t !== trait));
    } else {
      setTraits([...traits, trait]);
    }
  };

  if (error) {
    return (
      <JohariLayout
        nextSlide={() => null}
        showNextButton={false}
        disableNextButton={true}
        setHidden={false}
      >
        <Johari404 />
      </JohariLayout>
    );
  }

  const CurrentSlide = SLIDES[currentSlide];
  return (
    <>
      <Helmet>
        <title>
          {t('johariTitle', { firstName: data?.getJohariUser?.firstName })}
        </title>
      </Helmet>
      <JohariLayout
        nextSlide={nextSlide}
        showNextButton={!loading && currentSlide < 2}
        disableNextButton={
          loading ||
          isProcessing ||
          (currentSlide === 1 && traits.length < MAX_TRAITS)
        }
        setHidden={currentSlide === 1}
        currentSlide={currentSlide}
      >
        {(scrollToButton) => (
          <>
            {loading ? (
              <Loading />
            ) : (
              <CurrentSlide
                nextSlide={nextSlide}
                toggleTrait={toggleTrait}
                traits={traits}
                traitsLength={traits.length}
                // code={codeData?.getCodeByUserId}
                user={data?.getJohariUser}
                scrollToButton={scrollToButton}
              />
            )}
          </>
        )}
      </JohariLayout>
    </>
  );
};

const LoadingCont = styled.div`
  position: relative;
  width: 100%;
  align-items: center;
  justify-content: center;
  flex: 1;
`;

JohariPage.propTypes = {
  match: PropTypes.object,
};

export default JohariPage;
